/**
 * Date Helper Utilities for Infinity Date Handling
 *
 * Provides centralized utilities for handling PostgreSQL 'infinity' timestamps
 * in the frontend. These helpers enable consistent handling of infinite departure
 * dates for base tenant trips throughout the application.
 *
 * Key Concept: PostgreSQL's 'infinity' timestamptz value is serialized as the
 * string "infinity" in JSON responses. These utilities detect and handle this
 * special value appropriately.
 */

import { format, parseISO } from 'date-fns';

/**
 * Constant representing the infinity date string from PostgreSQL
 * @constant
 */
export const INFINITY_DATE_STRING = 'infinity';

/**
 * Checks if a date value represents an infinity date
 *
 * @param date - The date value to check (string, Date, null, or undefined)
 * @returns true if the value is the infinity string, false otherwise
 *
 * @example
 * isInfinityDate('infinity') // true
 * isInfinityDate('Infinity') // true (case insensitive)
 * isInfinityDate(' infinity ') // true (whitespace trimmed)
 * isInfinityDate('2025-01-01T10:00:00Z') // false
 * isInfinityDate(null) // false
 * isInfinityDate('') // false
 */
export function isInfinityDate(
  date: string | Date | null | undefined
): boolean {
  if (!date) return false;
  if (typeof date !== 'string') return false;
  // Trim whitespace and compare case-insensitively
  return date.trim().toLowerCase() === INFINITY_DATE_STRING;
}

/**
 * Safely parses a date value, returning null for infinity dates
 *
 * This function handles:
 * - Infinity strings → null (cannot be parsed as Date)
 * - Valid ISO strings → Date object
 * - Invalid strings → null
 * - Date objects → returned as-is
 * - null/undefined → null
 *
 * @param date - The date value to parse
 * @returns Parsed Date object or null
 *
 * @example
 * safeParseDate('infinity') // null
 * safeParseDate('2025-01-01T10:00:00Z') // Date object
 * safeParseDate('invalid') // null
 * safeParseDate(new Date()) // Date object (same instance)
 */
export function safeParseDate(
  date: string | Date | null | undefined
): Date | null {
  // Handle null/undefined
  if (!date) return null;

  // Handle infinity
  if (isInfinityDate(date)) return null;

  // Handle Date objects
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  // Parse ISO string
  try {
    const parsed = parseISO(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Formats a departure date for display, with special handling for infinity
 *
 * Returns:
 * - "No scheduled departure" for infinity dates
 * - Formatted date string (MMM d, HH:mm) for valid dates (24-hour format)
 * - "Invalid date" for invalid dates
 *
 * @param date - The date value to format
 * @returns Formatted date string
 *
 * @example
 * formatDepartureDate('infinity') // "No scheduled departure"
 * formatDepartureDate('2025-01-15T14:30:00Z') // "Jan 15, 14:30"
 * formatDepartureDate('invalid') // "Invalid date"
 * formatDepartureDate(null) // "Invalid date"
 */
export function formatDepartureDate(
  date: string | Date | null | undefined
): string {
  // Handle infinity
  if (isInfinityDate(date)) {
    return 'No scheduled departure';
  }

  // Parse the date
  const parsed = safeParseDate(date);

  // Handle invalid dates
  if (!parsed) {
    return 'Invalid date';
  }

  // Format using date-fns (24-hour format)
  try {
    return format(parsed, 'MMM d, HH:mm');
  } catch {
    return 'Invalid date';
  }
}

/**
 * Compares two date strings, with infinity treated as the largest value
 *
 * Returns:
 * - 1 if date1 > date2
 * - -1 if date1 < date2
 * - 0 if date1 === date2
 *
 * Special handling:
 * - infinity > any finite date
 * - infinity === infinity
 * - Invalid dates are treated as equal (returns 0)
 *
 * @param date1 - First date string
 * @param date2 - Second date string
 * @returns Comparison result (-1, 0, or 1)
 *
 * @example
 * compareDates('infinity', '2025-01-01T10:00:00Z') // 1
 * compareDates('2025-01-01T10:00:00Z', 'infinity') // -1
 * compareDates('infinity', 'infinity') // 0
 * compareDates('2025-01-01T10:00:00Z', '2025-12-31T10:00:00Z') // -1
 */
export function compareDates(date1: string, date2: string): number {
  const is1Infinity = isInfinityDate(date1);
  const is2Infinity = isInfinityDate(date2);

  // Both infinity
  if (is1Infinity && is2Infinity) return 0;

  // Only date1 is infinity (larger)
  if (is1Infinity) return 1;

  // Only date2 is infinity (smaller)
  if (is2Infinity) return -1;

  // Both are finite dates - parse and compare
  const parsed1 = safeParseDate(date1);
  const parsed2 = safeParseDate(date2);

  // Handle invalid dates - throw error instead of silently treating as equal
  if (!parsed1 && !parsed2) {
    // Both invalid - treat as equal for sorting stability
    return 0;
  }
  if (!parsed1) {
    throw new Error(`Invalid date string: "${date1}"`);
  }
  if (!parsed2) {
    throw new Error(`Invalid date string: "${date2}"`);
  }

  const time1 = parsed1.getTime();
  const time2 = parsed2.getTime();

  if (time1 > time2) return 1;
  if (time1 < time2) return -1;
  return 0;
}
